// Shared mapping from a git status code to a Lucide icon, a semantic color
// "tone", and a human label (spec 2.4.2). Used by the commit-detail changed-file
// rows today and reusable by the staging redesign (slice 9b). The tone maps to a
// `.mp-git-status--{tone}` class so color lives in the theme, not inline.

import type { GitStatusCode } from '@slate/core';
import {
  AlertTriangle,
  FilePen,
  Minus,
  Pencil,
  Plus,
  type LucideIcon,
} from 'lucide-react';

/** Semantic color tone for a status; drives the `.mp-git-status--{tone}` class. */
export type GitStatusTone =
  | 'modified'
  | 'added'
  | 'deleted'
  | 'renamed'
  | 'untracked'
  | 'conflict';

export interface GitStatusIcon {
  Icon: LucideIcon;
  tone: GitStatusTone;
  label: string;
}

/**
 * Resolve a git status code to its icon, tone and label. Unknown / unmodified
 * codes (`.`, `!`) fall back to the "modified" presentation so a row always
 * renders something sensible.
 */
export function gitStatusIcon(code: GitStatusCode): GitStatusIcon {
  switch (code) {
    case 'M':
      return { Icon: Pencil, tone: 'modified', label: 'Modified' };
    case 'A':
      return { Icon: Plus, tone: 'added', label: 'Added' };
    case 'D':
      return { Icon: Minus, tone: 'deleted', label: 'Deleted' };
    case 'R':
      return { Icon: FilePen, tone: 'renamed', label: 'Renamed' };
    case 'C':
      return { Icon: FilePen, tone: 'renamed', label: 'Copied' };
    case '?':
      return { Icon: Plus, tone: 'untracked', label: 'Untracked' };
    case 'U':
      return { Icon: AlertTriangle, tone: 'conflict', label: 'Conflict' };
    default:
      return { Icon: Pencil, tone: 'modified', label: 'Modified' };
  }
}
